import { Parser as FormulaParser } from 'hot-formula-parser';
import {
  Cell,
  CellTypes,
  FormulaErrorsOptions,
  PricingModelTableColumn,
  PricingModelTableRow,
  PricingModelTable,
  RowData,
} from '.';
import { Consumer } from '../common';
import { v4 as uuidv4 } from 'uuid';

const parser = new FormulaParser();

export interface PricingModelTableCalculationResult {
  table: PricingModelTable;
  pricingModelTableTotal: number;
  tableError: string;
}

export interface FormulaCalculationResult {
  error: string;
  result: number;
}

export function calculateFormula(rowKey: string, currentCell: Cell, cells: RowData): FormulaCalculationResult {
  let currentFormula = currentCell.formula;
  let localError = '';

  if (currentFormula && currentFormula[0] === '=') {
    currentFormula = currentFormula.slice(1);

    if (currentFormula?.includes(rowKey)) {
      return { error: FormulaErrorsOptions.REF, result: null };
    }

    Object.keys(cells).forEach((cellKey) => {
      if (currentFormula?.includes(cellKey)) {
        if (cells[cellKey]?.formula?.includes(rowKey)) {
          localError = FormulaErrorsOptions.REF;
        } else if (cells[cellKey]?.formula && rowKey != cellKey) {
          const ans = calculateFormula(cellKey, cells[cellKey], cells);
          if (ans.error) {
            localError = ans.error;
          } else {
            currentFormula = currentFormula?.replaceAll(cellKey, ans.result.toString());
          }
        } else {
          let cellValue = cells[cellKey].cellValue || 0;
          if (cells[cellKey].cellType === CellTypes.PERCENTAGE && cellValue) {
            cellValue = +cellValue / 100;
          }
          currentFormula = currentFormula?.replaceAll(cellKey, cellValue.toString());
        }
      }
    });
  }

  const { error, result } = parser.parse(currentFormula);
  return { error: localError || error, result: result };
}

export function tableCalculation(
  pricingModelTable: PricingModelTable,
  consumer?: Consumer,
): PricingModelTableCalculationResult {
  let tableError = '';
  let pricingModelTableTotal = 0;

  const calculatedRows = pricingModelTable.rows.map((row) => {
    const currentRow = { ...row };
    let currentCells = { ...row.cells };

    Object.keys(row.cells).forEach((rowKey) => {
      const cell = row.cells[rowKey];

      if (cell.cellType === CellTypes.FORMULA) {
        const formulaResult = calculateFormula(rowKey, cell, currentCells);
        currentCells = {
          ...currentCells,
          [rowKey]: { ...currentCells[rowKey], cellValue: formulaResult.error || formulaResult.result },
        };

        if (formulaResult.error) {
          tableError = formulaResult.error;
        }
      }
    });

    pricingModelTableTotal = pricingModelTableTotal + +currentCells['amount']?.cellValue;
    if (+currentCells['amount']?.cellValue !== 0) {
      // If the amount is greater than 0, then the row is not hidden in the invoice
      currentRow.isHiddenInInvoice = false;
    } else if (consumer == Consumer.RECEIVED_UI) {
      // If the amount is 0 and the caller is RECEIVED_UI, then the row is hidden in the invoice
      currentRow.isHiddenInInvoice = true;
    }

    return { ...currentRow, cells: currentCells };
  });

  return { table: { ...pricingModelTable, rows: calculatedRows }, pricingModelTableTotal, tableError };
}

export function getReferenceObjectIdsFromTable(pricingModelTable: PricingModelTable, cellType: CellTypes): string[] {
  if (!pricingModelTable) return [];

  return Array.from(
    new Set([
      ...getCellsPerType(pricingModelTable, cellType)
        .map((cellData) => cellData.refObjectId)
        .filter((data) => data),
    ]),
  );
}

export function getCellsPerType(pricingModelTable: PricingModelTable, cellType: CellTypes): Cell[] {
  return pricingModelTable.rows.flatMap((row: PricingModelTableRow) => {
    return Object.entries(row.cells)
      .map((cell) => cell[1])
      .filter((cell) => cell.cellType === cellType);
  });
}

export function getColumnPerType(pricingModelTable: PricingModelTable, cellType: CellTypes): PricingModelTableColumn[] {
  return pricingModelTable.columns.filter((column) => column.type === cellType);
}

export const defaultCol = {
  id: 'newcolumn',
  title: 'New column',
  type: CellTypes.NUMBER,
  isHidden: false,
  isHiddenInInvoice: false,
  isReadOnly: false,
};

export function addColumn(
  pricingModelTable: PricingModelTable,
  colIndex: number,
  column: PricingModelTableColumn = defaultCol,
) {
  const checkName = pricingModelTable.columns.filter((col) =>
    col.id.toLocaleLowerCase().replace(/\s/g, '').includes(column.id),
  );

  if (checkName.length) {
    let index = 1;
    checkName.forEach((col) => {
      const newIndex = Number(col.id.toLocaleLowerCase().replace(column.id, ''));

      if (newIndex >= index) {
        index = newIndex + 1;
      }
    });
    column = { ...column, id: `${column.id}${index}`, title: `New column ${index}` };
  }

  const newColumns = pricingModelTable.columns.map((col) => ({ ...col }));
  newColumns.splice(colIndex, 0, column);

  const newTable = {
    columns: newColumns,
    rows: pricingModelTable.rows.map((row) => {
      const newRow = {
        ...row,
        cells: {
          ...row.cells,
          [column.id]: {
            cellId: uuidv4(),
            cellType: column.type,
            cellValue: 0,
            isEditable: true,
            isHidden: false,
            isHiddenInInvoice: false,
            isReadOnly: false,
            refObjectId: column.refObjectId,
            refObject: undefined,
          },
        },
      };

      return newRow;
    }),
  };

  return newTable;
}
